import { GridActionsCellItem } from '@mui/x-data-grid/components';
import { GridColDef } from '@mui/x-data-grid/models/colDef/gridColDef';
import { Delete, Visibility } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { Link } from 'react-router-dom';
import { MaterialMeanResponse } from '@/api/bol';
import {
  formatDateTime,
  formatExternalReferences,
  formatValueWithUnit,
} from '@/utils/datagridFormatters';

interface MaterialMeanColumnsProps {
  onDelete: (materialMean: MaterialMeanResponse) => void;
  canDelete?: boolean;
  deleteTooltip?: string;
}

export const createMaterialMeanColumns = ({ onDelete, canDelete = true, deleteTooltip = '' }: MaterialMeanColumnsProps): GridColDef[] => [
  {
    field: 'id',
    headerName: 'ID',
    flex: 1.6,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { id?: string }) => row.id || '-',
  },
  {
    field: 'referenceId',
    headerName: 'Reference ID',
    flex: 1,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { referenceId?: string }) => row.referenceId || '-',
  },
  {
    field: 'registrationNumber',
    headerName: 'Registration Number',
    flex: 1,
    minWidth: 170,
    valueGetter: (_value: unknown, row: { registrationNumber?: string }) => row.registrationNumber || '-',
  },
  {
    field: 'meanType',
    headerName: 'Mean Type',
    flex: 1,
    minWidth: 130,
    valueGetter: (_value: unknown, row: { meanType?: string }) => row.meanType || '-',
  },
  {
    field: 'meanSubtype',
    headerName: 'Mean Subtype',
    flex: 1,
    minWidth: 150,
    valueGetter: (_value: unknown, row: { meanSubtype?: string }) => row.meanSubtype || '-',
  },
  {
    field: 'allocationDate',
    headerName: 'Allocation Date',
    flex: 1,
    minWidth: 160,
    valueGetter: (_value: unknown, row: { allocationDate?: string }) => formatDateTime(row.allocationDate),
  },
  {
    field: 'dimensionsVolume',
    headerName: 'Volume',
    flex: 0.9,
    minWidth: 120,
    valueGetter: (_value: unknown, row: { dimensions?: { volume?: { value?: number | null; unit?: string | null } } }) =>
      formatValueWithUnit(row.dimensions?.volume?.value, row.dimensions?.volume?.unit),
  },
  {
    field: 'piecesCount',
    headerName: 'Pieces',
    flex: 0.8,
    minWidth: 100,
    valueGetter: (_value: unknown, row: { pieces?: unknown[] }) => row.pieces?.length || 0,
  },
  {
    field: 'linkedResourcesCount',
    headerName: 'Linked Resources',
    flex: 0.9,
    minWidth: 140,
    valueGetter: (_value: unknown, row: { isLinkedToResources?: unknown[] }) => row.isLinkedToResources?.length || 0,
  },
  {
    field: 'externalReferences',
    headerName: 'External References',
    flex: 1.4,
    minWidth: 220,
    valueGetter: (_value: unknown, row: { externalReferences?: Array<{ externalReferenceType?: string | null; reference?: string | null }> }) =>
      formatExternalReferences(row.externalReferences),
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 120,
    getActions: (params) => [
      <Tooltip key="view" title="View details">
        <IconButton
          component={Link}
          to={`/material-means/${params.row.id as string}`}
          size="small"
          color="primary"
          aria-label="View Details"
        >
          <Visibility fontSize="small" />
        </IconButton>
      </Tooltip>,
      <GridActionsCellItem
        key="delete"
        icon={
          <Tooltip title={!canDelete ? deleteTooltip : 'Delete'}>
            <span>
              <Delete color={!canDelete ? 'disabled' : 'error'} />
            </span>
          </Tooltip>
        }
        label="Delete"
        onClick={() => onDelete(params.row as MaterialMeanResponse)}
        disabled={!canDelete}
        showInMenu={false}
      />,
    ],
  },
];

export const defaultMaterialMeanColumnVisibilityModel = {
  id: true,
  referenceId: true,
  registrationNumber: true,
  meanType: true,
  meanSubtype: true,
  allocationDate: true,
  dimensionsVolume: true,
  piecesCount: false,
  linkedResourcesCount: false,
  externalReferences: false,
};
